import { DiscoveredPropertyDetails, DiscoveredPropertyUpdate, HealthStatus, TagStatus } from '@admin/core/models';
import { HealthValues, TagStatusValues } from '@admin/shared/data';
import { formControl, Validators } from '@al00x/forms';
import { Component, inject } from '@angular/core';
import { CacBaseDialogComponent, CacDialogLayoutComponent, formBuilder, CacFormBuilderComponent, ButtonClickEvent, ItemRecord } from '@controladad/ng-base';
import { ImageViewerComponent, TreeViewComponent } from "@admin/shared";
import {  PersonnelApiService, PropertyApiService } from '@admin/core';
import { BehaviorSubject, take } from 'rxjs';

export type DiscoveredPropertyDialogData = DiscoveredPropertyDetails;
export type DiscoveredPropertyDialogResult = boolean;

@Component({
  selector: 'app-discovered-property-dialog',
  imports: [CacDialogLayoutComponent, ImageViewerComponent, CacFormBuilderComponent, TreeViewComponent],
  templateUrl: './discovered-property-dialog.component.html',
})
export class DiscoveredPropertyDialogComponent extends CacBaseDialogComponent<DiscoveredPropertyDialogData, DiscoveredPropertyDialogResult> {
  private readonly personnelApi = inject(PersonnelApiService);
  private readonly propertyApi = inject(PropertyApiService);

  locationControl = formControl<number | undefined>(undefined);

  personelItems$ = new BehaviorSubject<ItemRecord<number>[]>([]);

  formBuilder = formBuilder({
    cols: 2,
    inputs: {
      name: {
        control: formControl<string>(undefined, Validators.required),
        label: 'نام',
      },
      healthStatus: {
        control: formControl<HealthStatus>(undefined, Validators.required),
        label: 'وضعیت سلامت',
        type: 'select',
        searchable: false,
        clearable: false,
        items: HealthValues,
      },
      tagStatus: {
        control: formControl<TagStatus>(undefined, Validators.required),
        label: 'وضعیت تگ',
        type: 'select',
        searchable: false,
        clearable: false,
        items: TagStatusValues,
      },
      tagSerial: {
        control: formControl<string>(undefined),
        label: 'سریال تگ',
      },
      mainPersonnelId: {
        control: formControl<number | undefined>(undefined, Validators.required),
        label: 'تحویل گیرنده اصلی',
        type: 'select',
        items: this.personelItems$,
      },
      secondPersonnelId: {
        control: formControl<number | undefined>(undefined),
        label: 'تحویل گیرنده دوم',
        type: 'select',
        items: this.personelItems$,
      },
      discovererFullName: {
        control: formControl<string>(undefined),
        label: 'کشف کننده',
      },
      discoveredDate: {
        control: formControl<string>(undefined),
        label: 'تاریخ کشف',
        type: 'datetime',
      },
      description: {
        control: formControl<string>(undefined),
        label: 'توضیحات',
        type: 'textarea',
        colspan: 2,
      },
    },
  });

  constructor() {
    super();
    this.personnelApi.getPersonnelItems().pipe(take(1)).subscribe(res => {
      this.personelItems$.next(res);
    });
    const property = this.data;
    this.formBuilder.setValue({
      ...property,
      mainPersonnelId: property.personnel.at(0)?.id,
      secondPersonnelId: property.personnel.at(1)?.id,
    });
    this.formBuilder.setReadonly(true, 'discoveredDate', 'discovererFullName', 'description');
    this.locationControl.setValue(property.location?.id);
  }

  onSubmit(e: ButtonClickEvent) {
    const values = this.formBuilder.getValue();
    if (!values) return;
    const locationId = this.locationControl.value;
    if(!locationId) return;
    const personnelIds = [];
    if (values.mainPersonnelId) {
      personnelIds.push(values.mainPersonnelId);
    }
    if (values.secondPersonnelId) {
      personnelIds.push(values.secondPersonnelId);
    }
    const model: DiscoveredPropertyUpdate = {
      name: values.name,
      personnelIds,
      healthStatus: values.healthStatus,
      description: values.description,
      locationId,
      tagSerial: values.tagSerial,
      tagStatus: values.tagStatus,
    };
    this.propertyApi.updateDiscoveredProperty(this.data.id, model).pipe(e.pipe()).subscribe(() => {
      this.submit(true);
    });
  }
}
